import { type ReactNode } from 'react'

/**
 * Presentational shell for one calendar provider: a lettered logo, the provider
 * name with an optional status line, an optional badge (e.g. "Coming soon"), and
 * a body slot for the disclosure or connected details.
 */
export function ProviderCard(
	{ name, logo, statusLine, badge, provider, disabled = false, children }: {
		name: string
		logo: ReactNode
		statusLine?: ReactNode
		badge?: ReactNode
		provider?: string
		disabled?: boolean
		children?: ReactNode
	},
) {
	return (
		<div
			role="group"
			data-calendar-provider={provider}
			className={`rounded-lg border border-grey-200 p-5 ${disabled ? 'opacity-60' : ''}`}
		>
			<div className="flex items-start gap-4">
				<div className="shrink-0">{logo}</div>
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<span className="font-medium text-grey-900">{name}</span>
						{badge}
					</div>
					{statusLine
						? (
							<div role="status" aria-live="polite" className="mt-0.5 text-sm text-grey-500">
								{statusLine}
							</div>
						)
						: null}
					{children}
				</div>
			</div>
		</div>
	)
}

/** Rounded lettered logo used until we embed real brand marks. */
export function ProviderLogo({ letter, className }: { letter: string; className: string }) {
	return (
		<div
			className={`flex h-10 w-10 items-center justify-center rounded-lg text-lg font-semibold text-white ${className}`}
		>
			{letter}
		</div>
	)
}
